import { useEffect, useRef, useState } from 'react';
import type { BagItem } from '../../lib/bag';
import { CardapioError, placeMesaPedido } from '../../lib/api';
import { assinaturaDaSacola, chaveDoPedido, liberarChaveDoPedido } from '../../lib/chavePendente';
import { formatBRL } from '../../lib/format';
import { useDialogA11y } from '../../hooks/useDialogA11y';

interface Props {
  codigo: string;
  token: string;
  bag: BagItem[];
  subtotal: number;
  mesaNumero: number;
  /** F3-M40 — TAXA DE CONSUMO (%) que a loja VAI cobrar sobre os itens (vem da entrada na mesa; é o MESMO
   *  percentual que o backend snapshota no pedido). Ausente/0 = loja sem taxa: a tela fica como antes. */
  taxaConsumoPercentual?: number;
  onClose: () => void;
  onPlaced: () => void;
  /** QA lote12 #8 — sessão da mesa revogada/expirada (401 → CardapioError 'unavailable'): não há como
   *  reenviar; avisa a MesaPage para limpar a sessão morta e reexibir o formulário de entrar. */
  onSessionExpired?: () => void;
  /** O servidor recusou um item (409 PRODUTO_*): o pai recarrega o catálogo e volta à sacola (QA 31/10/2026). */
  onItemIndisponivel?: () => void;
}

// F3-M40 — percentual como rótulo: inteiro sem casas ("10"), fracionário com uma ("12,5"). O rótulo é
// "Taxa de consumo", o MESMO que o PDV (formatTaxaConsumoLabel) e o cupom usam para esta cobrança — é o
// snapshot `taxa_consumo_percentual` do pedido (V202/V203), e o cliente tem de ler no papel o mesmo nome
// que leu na tela.
function formatPercentual(p: number): string {
  const arred = Math.round(p * 10) / 10;
  return Number.isInteger(arred) ? String(arred) : arred.toFixed(1).replace('.', ',');
}

// Valor da taxa sobre o subtotal, em centavos fechados. HALF_EVEN (banker's) porque é o modo do motor de
// dinheiro do backend (CalculadoraTotalComanda usa RoundingMode.HALF_EVEN, escala 2): com HALF_UP o
// meio-centavo exato daria 1 centavo a mais do que a comanda vai cobrar, e a tela erraria de novo — só
// menos. Percentual ausente/zero/negativo = SEM taxa.
function valorTaxaConsumo(subtotal: number, percentual: number): number {
  if (!(percentual > 0) || !(subtotal > 0)) return 0;
  const centavos = (subtotal * percentual) / 100 * 100;
  const piso = Math.floor(centavos);
  const resto = centavos - piso;
  const arred =
    Math.abs(resto - 0.5) < 1e-9 ? (piso % 2 === 0 ? piso : piso + 1) : Math.round(centavos);
  return arred / 100;
}

export function MesaOrderConfirm(props: Props) {
  const percentual = props.taxaConsumoPercentual ?? 0;
  const taxaValor = valorTaxaConsumo(props.subtotal, percentual);
  const [observacoes, setObservacoes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Pedido de mesa é um Pedido REAL (sem número sequencial) — a confirmação é um booleano de sucesso,
  // não um número. O gate anterior (numero != null) nunca acendia porque a resposta não traz `numero`.
  const [enviado, setEnviado] = useState(false);
  // idempotencyKey ESTÁVEL do pedido de mesa: reusada em cada re-tentativa da MESMA sacola, inclusive depois de
  // fechar e reabrir esta confirmação (guardada na aba, ver lib/chavePendente). Sem isto, uma queda de rede após o
  // backend já ter registrado o pedido levaria o retry a DUPLICAR o pedido (debita estoque 2×, imprime 2×). Só
  // renova após o sucesso confirmado ou quando a sacola muda.
  const escopoChave = `mesa:${props.codigo}:${props.token}`;
  const dialogRef = useRef<HTMLElement | null>(null);
  // QA-28 M-07 — Tab preso ao diálogo + cardápio de fundo `aria-hidden` + foco de volta no gatilho.
  useDialogA11y(dialogRef);

  // QA-28 M-07 — Esc fecha (paridade com os outros overlays), NUNCA durante o envio: desmontar no meio do envio
  // deixaria o cliente sem saber se o pedido entrou (a chave fica guardada na aba, mas o desfecho se perde).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) props.onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, props]);

  async function submit() {
    setBusy(true);
    setError('');
    const idempotencyKey = chaveDoPedido(escopoChave, `${assinaturaDaSacola(props.bag)}#${observacoes.trim()}`);
    try {
      await placeMesaPedido(
        props.codigo,
        props.token,
        props.bag,
        observacoes,
        idempotencyKey
      );
      setEnviado(true);
      liberarChaveDoPedido(escopoChave); // sucesso → o próximo pedido usa uma chave nova
      props.onPlaced();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível enviar o pedido.');
      // QA lote12 #8 — sessão revogada/expirada (401/403/404 → CardapioError 'unavailable'): reenviar
      // não adianta. Propaga para a MesaPage limpar a sessão morta e reabrir o formulário de entrar,
      // dando ao cliente um caminho claro de reautenticação em vez de travar na mensagem de erro.
      // Item recusado (esgotou, saiu do cardápio ou foi excluído): nada foi registrado com esta chave. Libera a
      // chave e devolve o cliente à sacola com o catálogo recarregado, onde o item aparece com "Remover".
      if (e instanceof CardapioError && e.status === 409 && (e.code ?? '').startsWith('PRODUTO_')) {
        liberarChaveDoPedido(escopoChave);
        props.onItemIndisponivel?.();
      }
      if (e instanceof CardapioError && e.kind === 'unavailable') {
        liberarChaveDoPedido(escopoChave); // recusado pelo servidor: nada foi registrado com esta chave
        props.onSessionExpired?.();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    // QA L3 — NÃO deixa fechar (backdrop/×) enquanto `busy`: dispensar um envio em andamento desmontava o
    // modal e o cliente perdia o desfecho do envio (a chave de idempotência agora fica guardada na aba, então o
    // reenvio deduplica). Só fecha quando não está enviando.
    <div
      className="loja-overlay loja-overlay--center"
      onClick={(e) => e.target === e.currentTarget && !busy && props.onClose()}
    >
      <section ref={dialogRef} className="mesa-confirm" role="dialog" aria-modal="true" aria-label="Confirmar pedido">
        <button className="loja-close mesa-close" onClick={props.onClose} aria-label="Fechar" disabled={busy}>×</button>
        {enviado ? (
          <div className="mesa-success">
            <span className="mesa-success-icon">✓</span>
            <small>MESA {props.mesaNumero}</small>
            <h2>Enviado para a cozinha</h2>
            <p>Agora é só aproveitar. Você pode fazer novos pedidos enquanto a conta estiver aberta.</p>
            <button className="loja-btn loja-btn--primary loja-btn--block" onClick={props.onClose}>
              Voltar ao cardápio
            </button>
          </div>
        ) : (
          <>
            <div className="mesa-modal-head">
              <span className="mesa-kicker">MESA {props.mesaNumero}</span>
              <h2>Revise seu pedido</h2>
              <p>Os valores serão confirmados pelo restaurante antes do envio.</p>
            </div>
            <div className="mesa-order-list">
              {props.bag.map((item) => {
                // QA L2 — descreve variação + adicionais (como a sacola/checkout): linhas do MESMO produto
                // com configs diferentes deixam de parecer idênticas na revisão antes de enviar à cozinha.
                const cfg = [...(item.pizzaResumo ?? []), item.variacao?.nome, ...item.adicionais.map((a) => a.nome)].filter(Boolean).join(' · ');
                return (
                  <div key={item.key}>
                    <span>
                      <b>{item.qty}×</b> {item.produto.nome}
                      {cfg && <small> · {cfg}</small>}
                    </span>
                    <strong>{formatBRL(item.unitPrice * item.qty)}</strong>
                  </div>
                );
              })}
            </div>
            <label className="mesa-field">
              <span>Observação para a cozinha <small>opcional</small></span>
              <textarea
                maxLength={1000}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Ex.: sem cebola, ponto da carne..."
              />
            </label>
            {/* F3-M40 — a TAXA DE CONSUMO que a loja cobra entra ANTES do total, com o percentual escrito.
                Antes, uma loja com 10% ligado mostrava "Total estimado R$ 100,00" e a comanda fechava em
                R$ 110,00: o cliente descobria o acréscimo no caixa. O backend já snapshotava o percentual
                no pedido (RegistrarPedidoMesaService) — faltava o número CHEGAR na tela.
                O valor é calculado sobre o subtotal desta revisão, como o backend faz por pedido
                (CalculadoraTotalComanda.somaTaxaConsumo: valorTotal x %/100). É estimativa, como o total:
                o servidor é a autoridade e o consumo da mesa pode crescer depois. */}
            {taxaValor > 0 && (
              <>
                <div className="loja-sumline">
                  <span>Itens</span>
                  <b>{formatBRL(props.subtotal)}</b>
                </div>
                <div className="loja-sumline">
                  <span>Taxa de consumo ({formatPercentual(percentual)}%)</span>
                  <b>{formatBRL(taxaValor)}</b>
                </div>
              </>
            )}
            <div className="mesa-total">
              <span>Total estimado</span>
              <b>{formatBRL(props.subtotal + taxaValor)}</b>
            </div>
            {error && <p className="mesa-error" role="alert">{error}</p>}
            <button className="loja-btn loja-btn--primary loja-btn--block loja-btn--lg" disabled={busy} onClick={submit}>
              {busy ? 'Enviando…' : 'Enviar para a cozinha'}
            </button>
          </>
        )}
      </section>
    </div>
  );
}
